import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails, Table, TableBody,
  TableCell, TableHead, TableRow, Alert, CircularProgress, Chip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';

const PAGE_SIZE = 10;
const LIST_HEIGHT = 480;
const SCROLL_BOTTOM_THRESHOLD = 80; // px จากขอบล่างที่เริ่มโหลดหน้าถัดไป

export function CustomerPaymentAccordionList({
  customerId, label, projectName, projectNumber, dateFrom, ocrEnabled = true,
  onTotal, onTotalSinceCutoff, onUnverifiedTotal,
}) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  // สถานะ "ยืนยันแล้ว/ยังไม่ยืนยัน" แบบเดียวกับที่ใช้ใน LoanProjectExpenseList.js/
  // LoanLedgerTable.js — Payment ก่อนวันกำหนดยอดคงเหลือถือว่ายืนยันแล้วอัตโนมัติ
  // (นับรวมอยู่ในยอดตั้งต้นไปแล้ว) ที่เหลือเช็ค ocr_verified_loan_pair จริง
  const isVerified = useCallback(
    (payment) => !ocrEnabled
      || (dateFrom && new Date(payment.date) < new Date(dateFrom))
      || !!payment.ocr_verified_loan_pair,
    [dateFrom, ocrEnabled],
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // ยอดรวมของ Payment ทั้งหมดที่ตรงเงื่อนไข (มาจาก backend คำนวณล่วงหน้าแล้ว ไม่ใช่
  // ผลรวมของ rows ที่โหลดมาแล้วเท่านั้น — เพราะ infinite-scroll โหลดทีละหน้า ถ้ารวมจาก
  // rows เฉยๆ ตัวเลขจะยังไม่ครบจนกว่าจะเลื่อนดูจนสุด)
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const loadPage = useCallback(async (pageToLoad, append) => {
    if (!customerId) return;
    if (append) setLoadingMore(true); else setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listPaymentsByCustomer({
        customerId, page: pageToLoad, size: PAGE_SIZE, projectName, projectNumber, dateFrom,
      }));
      setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
      setTotal(data.total);
      setTotalAmount(data.totalAmount || 0);
      if (onTotal) onTotal(data.totalAmount || 0);
      if (onTotalSinceCutoff) onTotalSinceCutoff(data.totalAmountSinceCutoff || 0);
      if (onUnverifiedTotal) onUnverifiedTotal(data.unverifiedTotal || 0);
      setPage(pageToLoad);
    } catch (err) {
      setError(err.message);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [customerId, dispatch, projectName, projectNumber, dateFrom, onTotal, onTotalSinceCutoff, onUnverifiedTotal]);

  // customer เปลี่ยน (สลับกลุ่มบัญชี/สลับฝั่งยืม-คืน) → เริ่มลิสต์ใหม่ตั้งแต่หน้า 1
  useEffect(() => {
    setRows([]);
    setTotal(0);
    setTotalAmount(0);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, projectName, projectNumber, dateFrom]);

  const hasMore = rows.length < total;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < SCROLL_BOTTOM_THRESHOLD) {
      loadPage(page + 1, true);
    }
  };

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: LIST_HEIGHT }}>
      {label && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 2, py: 1.5, flexShrink: 0 }}>
          <Typography variant="subtitle1">{label}</Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            รวม {formatCurrency(totalAmount)} บาท
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{error}</Alert>}

      <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                ไม่มีรายการ Payment ที่สำเร็จแล้วของ customer นี้
              </Typography>
            )}
            {rows.map((payment) => (
              <Accordion key={payment._id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography>
                      {payment.payment_number} — {new Date(payment.date).toLocaleDateString('th-TH')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isVerified(payment) ? (
                        <Chip size="small" color="success" label="ยืนยันแล้ว" />
                      ) : (
                        <Chip size="small" color="warning" label="ยังไม่ยืนยัน" />
                      )}
                      <Typography>{formatCurrency(payment.totalAmount)}</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>รายการ (Expense)</TableCell>
                        <TableCell>โครงการ</TableCell>
                        <TableCell>งบประมาณ</TableCell>
                        <TableCell align="right">จำนวนเงิน</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(payment.expenses_list || []).map((item, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow key={item._id || idx}>
                          <TableCell>{item.name || '-'}</TableCell>
                          <TableCell>{item.project?.name || '-'}</TableCell>
                          <TableCell>
                            {item.budget?.prefix || item.budget?.budget_number
                              ? `${item.budget?.prefix || ''}${item.budget?.budget_number || ''}`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(item.total ?? item.price)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}

            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!hasMore && rows.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1.5 }}>
                — สิ้นสุดรายการ ({total} รายการ) —
              </Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}

export default CustomerPaymentAccordionList;
